import { useCallback } from "react"
import { saveAs } from 'file-saver'
import { useRelatoriosDashApi } from "../../infra/relatorios-dash"
import { useAppDispatch } from "../../presentation/hooks/store-hooks"
import { useUIService } from "../ui/useUIService"
import { 
    IRelatorioAcessRequest,
    IRelatorioQuantitativeRequest,
    IRelatorioSynteticRequest,
    IRelatorioReceitaRequest,
    IRelatorioConsumoAnalyticRequest,
    IRelatorioItem,
    IRelatorioComMudancas
} from "../../application/models/IRelatorioDash"
import {
    setRelatorioAcess,
    setRelatorioQuantitative,
    setRelatorioSyntetic,
    setRelatorioReceita,
    setRelatorioConsumoAnalytic,
    setMeusRelatorios
} from "../../application/states/relatorios-dash"

export const ordenarColunasRelatorio = (keys: string[], dados: Record<string, unknown>[]): string[] => {
    const camposExcluidos = ['IdTipoConsumo', 'IdProduto', 'IdCliente', 'OrderColumn']
    const camposFiltrados = keys.filter(key => !camposExcluidos.includes(key))

    const camposNaoNumericos: string[] = []
    const camposNumericos: string[] = []

    camposFiltrados.forEach(key => {
        const temValoresNumericos = dados.some(row => {
            const value = row[key]
            const numValue = typeof value === 'number' ? value : parseFloat(String(value))
            return !isNaN(numValue) && isFinite(numValue)
        })
        if (temValoresNumericos) {
            camposNumericos.push(key)
        } else {
            camposNaoNumericos.push(key)
        }
    })

    camposNumericos.sort((a, b) => {
        if (a.toLowerCase().includes('quantidade')) return 1
        if (b.toLowerCase().includes('quantidade')) return -1
        return 0
    })

    return [...camposNaoNumericos, ...camposNumericos]
}

export const calcularTotalQuantidadeRelatorio = (dados: Record<string, unknown>[]): number => {
    if (!dados?.length) return 0

    const keys = Object.keys(dados[0])
    const colunasOrdenadas = ordenarColunasRelatorio(keys, dados)
    const ultimaColuna = colunasOrdenadas[colunasOrdenadas.length - 1]
    if (!ultimaColuna) return 0

    let total = 0
    dados.forEach(row => {
        const value = row[ultimaColuna]
        const numValue = typeof value === 'number' ? value : parseFloat(String(value))
        if (!isNaN(numValue) && isFinite(numValue)) {
            total += numValue
        }
    })
    return total
}

// use-cases/relatorios-dash/useRelatoriosDashService.ts
export const useRelatoriosDashService = () => {
    const dispatch = useAppDispatch()
    const { setLoading, setAlert } = useUIService()
    const {
        getRelatorioAcessApi,
        getRelatorioQuantitativeApi,
        getRelatorioSynteticApi,
        getRelatorioReceitaApi,
        getRelatorioConsumoAnalyticApi,
        getRelatorioFilaStatusApi,
        getMeusRelatoriosApi,
        reprocessarRelatorioApi,
        downloadRelatorioArquivoApi
    } = useRelatoriosDashApi()

    const getRelatorioAcess = useCallback(async (data: IRelatorioAcessRequest) => {
        setLoading({ loading: true, message: 'Gerando relatório de acessos...', id: 'getRelatorioAcess' })
        try {
            const result = await getRelatorioAcessApi(data)
            const raw = result.data
            const payload = (raw && typeof raw === 'object' && Array.isArray(raw.data) && (raw.status !== undefined || raw.filaId !== undefined))
                ? raw
                : (raw?.data ?? raw)
            dispatch(setRelatorioAcess(payload))
            return result
        } catch (error: any) {
            console.error('❌ Erro no useRelatoriosDashService:', error);
            const message = error?.response?.data?.message || 'Erro ao gerar relatório de acessos'
            setAlert({ message, type: 'error' })
            throw error
        } finally {
            setLoading({ loading: false, id: 'getRelatorioAcess' })
        }
    }, [getRelatorioAcessApi, dispatch, setLoading, setAlert])

    const getRelatorioQuantitative = useCallback(async (data: IRelatorioQuantitativeRequest) => {
        setLoading({ loading: true, message: 'Gerando relatório quantitativo...', id: 'getRelatorioQuantitative' })
        try {
            const result = await getRelatorioQuantitativeApi(data)
            dispatch(setRelatorioQuantitative(result.data))
            return result
        } catch (error: any) {
            const message = error?.response?.data?.message || 'Erro ao gerar relatório quantitativo'
            setAlert({ message, type: 'error' })
            throw error
        } finally {
            setLoading({ loading: false, id: 'getRelatorioQuantitative' })
        }
    }, [getRelatorioQuantitativeApi, dispatch, setLoading, setAlert])

    const getRelatorioSyntetic = useCallback(async (data: IRelatorioSynteticRequest)     => {
        setLoading({ loading: true, message: 'Gerando relatório sintético...', id: 'getRelatorioSyntetic' })
        try {
            const result = await getRelatorioSynteticApi(data)
            dispatch(setRelatorioSyntetic(result.data))
            return result
        } catch (error: any) {
            const message = error?.response?.data?.message || 'Erro ao gerar relatório sintético'
            setAlert({ message, type: 'error' })
            throw error
        } finally {
            setLoading({ loading: false, id: 'getRelatorioSyntetic' })
        }
    }, [getRelatorioSynteticApi, dispatch, setLoading, setAlert])

    const getRelatorioReceita = useCallback(async (data: IRelatorioReceitaRequest) => {
        setLoading({ loading: true, message: 'Gerando relatório de receita...', id: 'getRelatorioReceita' })
        try {
            const result = await getRelatorioReceitaApi(data)
            dispatch(setRelatorioReceita(result))
            return result
        } catch (error: any) {
            const message = error?.response?.data?.message || 'Erro ao gerar relatório de receita'
            setAlert({ message, type: 'error' })
            throw error
        } finally {
            setLoading({ loading: false, id: 'getRelatorioReceita' })
        }
    }, [getRelatorioReceitaApi, dispatch, setLoading, setAlert])

    const getRelatorioConsumoAnalytic = useCallback(async (data: IRelatorioConsumoAnalyticRequest) => {
        setLoading({ loading: true, message: 'Gerando relatório de consumo analítico...', id: 'getRelatorioConsumoAnalytic' })
        try {
            const result = await getRelatorioConsumoAnalyticApi(data)
            dispatch(setRelatorioConsumoAnalytic(result.data))
            return result
        } catch (error: any) {
            const message = error?.response?.data?.message || 'Erro ao gerar relatório de consumo analítico'
            setAlert({ message, type: 'error' })
            throw error
        } finally {
            setLoading({ loading: false, id: 'getRelatorioConsumoAnalytic' })
        }
    }, [getRelatorioConsumoAnalyticApi, dispatch, setLoading, setAlert])

    const getRelatorioFilaStatus = useCallback(async (id: number) => {
        try {
            const result = await getRelatorioFilaStatusApi(id)
            return result
        } catch (error) {
            throw error
        }
    }, [getRelatorioFilaStatusApi])

    const getMeusRelatorios = useCallback(async (diasRetencao?: number, options?: { silent?: boolean }) => {
        const silent = options?.silent ?? false
        if (!silent) {
            setLoading({ loading: true, message: 'Buscando relatórios...', id: 'getMeusRelatorios' })
        }
        try {
            const result = await getMeusRelatoriosApi(diasRetencao)
            return result
        } catch (error) {
            if (!silent) setAlert({ message: 'Erro ao buscar relatórios', type: 'error' })
            throw error
        } finally {
            if (!silent) setLoading({ loading: false, id: 'getMeusRelatorios' })
        }
    }, [getMeusRelatoriosApi, setLoading, setAlert])

    const reprocessarRelatorio = useCallback(async (idRelatorioFila: number) => {
        setLoading({ loading: true, message: 'Reprocessando relatório...', id: 'reprocessarRelatorio' })
        try {
            const result = await reprocessarRelatorioApi(idRelatorioFila)
            setAlert({ message: 'Relatório enviado para reprocessamento', type: 'success' })
            return result
        } catch (error) {
            setAlert({ message: 'Erro ao reprocessar relatório', type: 'error' })
            throw error
        } finally {
            setLoading({ loading: false, id: 'reprocessarRelatorio' })
        }
    }, [reprocessarRelatorioApi, setLoading, setAlert])

    const downloadArquivoRelatorio = useCallback(async (
        file: { filename: string; downloadUrl: string },
        onUpdateRelatorios?: (relatorios: IRelatorioItem[]) => void,
        onFileNotFound?: () => void
    ): Promise<void> => {
        try {
            const blob = await downloadRelatorioArquivoApi(file.downloadUrl)
            saveAs(blob, file.filename)
        } catch (error: any) {
            const status = error?.response?.status
            if (status === 404) {
                onFileNotFound?.()
                try {
                    const relatoriosResult = await getMeusRelatoriosApi(90)
                    if (relatoriosResult?.success && relatoriosResult?.data?.relatorios) {
                        if (onUpdateRelatorios) {
                            onUpdateRelatorios(relatoriosResult.data.relatorios)
                        }
                        dispatch(setMeusRelatorios(relatoriosResult.data.relatorios))
                    }
                } catch {
                    /* falha ao atualizar lista tratada silenciosamente */
                }
                return
            }
            throw error
        }
    }, [downloadRelatorioArquivoApi, getMeusRelatoriosApi, dispatch])

    const reprocessarRelatorioComPolling = useCallback(async (
        idRelatorioFila: number,
        onUpdateRelatorios: (relatorios: IRelatorioItem[]) => void,
        onFilaIdChange: (filaId: number | null) => void,
        pollingInterval: number = 3000,
        timeout: number = 60000
    ): Promise<number | null> => {
        try {
            const result = await reprocessarRelatorioApi(idRelatorioFila)
            
            if (result?.success && result?.data?.filaId) {
                const novaFilaId = result.data.filaId
                const relatoriosResult = await getMeusRelatoriosApi(90)
                if (relatoriosResult?.success && relatoriosResult?.data?.relatorios) {
                    onUpdateRelatorios(relatoriosResult.data.relatorios)
                }
                onFilaIdChange(novaFilaId)
                
                const quickPollInterval = setInterval(async () => {
                    const quickResult = await getMeusRelatoriosApi(90)
                    if (quickResult?.success && quickResult?.data?.relatorios) {
                        onUpdateRelatorios(quickResult.data.relatorios)
                        
                        const relatorioAtualizado = quickResult.data.relatorios.find(
                            (r: IRelatorioItem) => r.IdRelatorioFila === novaFilaId
                        )
                        
                        if (relatorioAtualizado) {
                            const processadoComArquivo = relatorioAtualizado.statusText === 'PROCESSADO' && 
                                relatorioAtualizado.hasFiles && 
                                relatorioAtualizado.files && 
                                relatorioAtualizado.files.length > 0
                            
                            const erro = relatorioAtualizado.statusText === 'ERRO'
                            if (processadoComArquivo || erro) {
                                clearInterval(quickPollInterval)
                                onFilaIdChange(null)
                            }
                        }
                    }
                }, pollingInterval)
                
                setTimeout(() => {
                    clearInterval(quickPollInterval)
                    onFilaIdChange(null)
                }, timeout)
                
                return novaFilaId
            }
            
            return null
        } catch (error) {
            throw error
        }
    }, [reprocessarRelatorioApi, getMeusRelatoriosApi])

    const temRelatoriosProcessando = useCallback((relatorios: IRelatorioItem[]): boolean => {
        return relatorios.some((r) => 
            r.statusText === 'EM_FILA' || r.statusText === 'EM_ANDAMENTO'
        )
    }, [])

    const verificarMudancasRelatorios = useCallback((
        relatoriosAtuais: IRelatorioItem[],
        relatoriosAnteriores: IRelatorioItem[]
    ): IRelatorioComMudancas[] => {
        return relatoriosAtuais.map((r) => {
            const relatorioAnterior = relatoriosAnteriores.find(
                (prev) => prev.IdRelatorioFila === r.IdRelatorioFila
            )
            
            const statusMudou = !relatorioAnterior || relatorioAnterior.statusText !== r.statusText
            
            const perdeuArquivo = relatorioAnterior && 
                relatorioAnterior.hasFiles && 
                (!r.hasFiles || !r.files || r.files.length === 0)
            
            const ganhouArquivo = relatorioAnterior && 
                (!relatorioAnterior.hasFiles || !relatorioAnterior.files || relatorioAnterior.files.length === 0) &&
                r.hasFiles && r.files && r.files.length > 0
            
            return {
                ...r,
                _deveAtualizar: !!(statusMudou || perdeuArquivo || ganhouArquivo)
            } as IRelatorioComMudancas
        })
    }, [])

    const algumRelatorioPerdeuArquivo = useCallback((
        relatoriosAtuais: IRelatorioComMudancas[],
        relatoriosAnteriores: IRelatorioItem[]
    ): boolean => {
        return relatoriosAtuais.some((r) => 
            r._deveAtualizar && 
            relatoriosAnteriores.some((prev) => 
                prev.IdRelatorioFila === r.IdRelatorioFila && 
                prev.hasFiles && 
                (!r.hasFiles || !r.files || r.files.length === 0)
            )
        )
    }, [])

    const deveAtualizarListaRelatorios = useCallback((
        relatoriosAtuais: IRelatorioComMudancas[],
        relatoriosAnteriores: IRelatorioItem[]
    ): boolean => {
        const temProcessando = temRelatoriosProcessando(relatoriosAtuais)
        const perdeuArquivo = algumRelatorioPerdeuArquivo(relatoriosAtuais, relatoriosAnteriores)
        const temMudancas = relatoriosAtuais.some((r) => r._deveAtualizar)
        
        return temProcessando || perdeuArquivo || temMudancas
    }, [temRelatoriosProcessando, algumRelatorioPerdeuArquivo])

    const filaIdAindaProcessando = useCallback((
        filaId: number | null | undefined,
        relatorios: IRelatorioItem[]
    ): boolean => {
        if (!filaId) return false
        return relatorios.some((r) => 
            r.IdRelatorioFila === filaId && 
            (r.statusText === 'EM_FILA' || r.statusText === 'EM_ANDAMENTO')
        )
    }, [])

    const filaIdProcessadoComArquivo = useCallback((
        filaId: number | null | undefined,
        relatorios: IRelatorioItem[]
    ): boolean => {
        if (!filaId) return false
        const relatorio = relatorios.find((r) => r.IdRelatorioFila === filaId)
        return !!(
            relatorio &&
            relatorio.statusText === 'PROCESSADO' &&
            relatorio.hasFiles &&
            relatorio.files &&
            relatorio.files.length > 0
        )
    }, [])

    return {
        getRelatorioAcess,
        getRelatorioQuantitative,
        getRelatorioSyntetic,
        getRelatorioReceita,
        getRelatorioConsumoAnalytic,
        getRelatorioFilaStatus,
        getMeusRelatorios,
        reprocessarRelatorio,
        downloadArquivoRelatorio,
        reprocessarRelatorioComPolling,
        ordenarColunasRelatorio,
        calcularTotalQuantidadeRelatorio,
        temRelatoriosProcessando,
        verificarMudancasRelatorios,
        algumRelatorioPerdeuArquivo,
        deveAtualizarListaRelatorios,
        filaIdAindaProcessando,
        filaIdProcessadoComArquivo
    }
}